import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, setAuth, clearAuth, getAuth, getVehicles } from './utils/api';

import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export default function App() {
  // Lazy-init from localStorage so the user stays logged in across refreshes.
  const [user, setUser] = useState(() => getAuth());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Only validate when restoring a stored session — not on a fresh login.
  const [validating, setValidating] = useState(() => !!getAuth());

  // When a stored session is found on mount, verify it is still alive.
  // Real auth failures (result:-3, HTTP 401) are caught by the axios interceptor
  // which fires mdvr:session-expired — the catch here must not clear auth on
  // transient network errors or the user gets logged out whenever the proxy is
  // briefly unreachable (e.g. container restart).
  useEffect(() => {
    if (!validating) return;
    getVehicles()
      .catch(() => { /* network error — keep auth, interceptor handles auth failures */ })
      .finally(() => setValidating(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The axios interceptor fires this event when CMSV6 returns result:-3 or HTTP 401.
  useEffect(() => {
    const handle = () => {
      setUser(null);
      setError('Session expired. Please log in again.');
    };
    window.addEventListener('mdvr:session-expired', handle);
    return () => window.removeEventListener('mdvr:session-expired', handle);
  }, []);

  const login = useCallback(async (account, password) => {
    setLoading(true); setError('');
    try {
      const data = await apiLogin(account, password);
      if (data.success) {
        setAuth(data);
        setUser(data);
      } else {
        setError(data.msg || 'Login failed');
      }
    } catch {
      setError('Cannot reach server. Check proxy is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout().catch(() => {});
    clearAuth();
    setUser(null);
    setError('');
  }, []);

  if (validating) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui,sans-serif', fontSize:13, color:'#888' }}>
        Restoring session…
      </div>
    );
  }

  if (!user) return <LoginPage onLogin={login} loading={loading} error={error} />;

  return (
    <AuthContext.Provider value={{ user, logout }}>
      <Dashboard />
    </AuthContext.Provider>
  );
}
